import { tx } from './i18n';

// API and database codes stay canonical. Only their visible labels are
// resolved here, so filters, payloads, indexes and stored values never depend
// on the selected interface language.
const GROUPS = Object.freeze({
  status: Object.freeze({
    active: 'ui.values.active',
    inactive: 'ui.values.inactive',
    pending: 'ui.values.pending',
    online: 'ui.values.online',
    offline: 'ui.values.offline',
    disabled: 'ui.values.disabled',
    draft: 'ui.values.draft',
    retired: 'ui.values.retired',
    decommissioned: 'ui.values.decommissioned',
    superseded: 'ui.values.superseded',
    running: 'ui.values.running',
    failed: 'ui.values.failed',
    passed: 'ui.values.passed',
    warning: 'ui.values.warning',
    skipped: 'ui.values.skipped',
    complete: 'ui.values.complete',
    completed: 'ui.values.completed',
    cancelled: 'ui.values.cancelled',
    canceled: 'ui.values.cancelled',
    queued: 'ui.values.queued',
    assigned: 'ui.values.assigned',
    downloading: 'ui.values.downloading',
    downloaded: 'ui.values.downloaded',
    verified: 'ui.values.verified',
    ready: 'ui.values.ready',
    succeeded: 'ui.values.succeeded',
    timed_out: 'ui.values.timed_out',
    offloading: 'ui.values.offloading',
    importing: 'ui.values.importing',
    not_required: 'ui.values.not_required',
    awaiting_offload: 'ui.values.awaiting_offload',
    redeemed: 'ui.values.redeemed',
    revoked: 'ui.values.revoked',
    expired: 'ui.values.expired',
  }),
  contentKind: Object.freeze({
    report: 'ui.values.content_report',
    service: 'ui.values.content_service',
  }),
  locationKind: Object.freeze({
    office: 'ui.values.location_office',
    laboratory: 'ui.values.location_laboratory',
    warehouse: 'ui.values.location_warehouse',
    retail: 'ui.values.location_retail',
  }),
  productType: Object.freeze({
    kit: 'ui.values.product_kit',
    supplement: 'ui.values.product_supplement',
    wearable: 'ui.values.product_wearable',
    merch: 'ui.values.product_merchandise',
    service: 'ui.values.product_service',
    subscription: 'ui.values.product_subscription',
  }),
  nodeRole: Object.freeze({
    worker: 'ui.values.node_worker',
    storage: 'ui.values.node_storage',
  }),
  paymentStatus: Object.freeze({
    requires_payment_method: 'ui.values.payment_requires_payment_method',
    requires_confirmation: 'ui.values.payment_requires_confirmation',
    requires_action: 'ui.values.payment_requires_action',
    processing: 'ui.values.payment_processing',
    requires_capture: 'ui.values.payment_requires_capture',
    canceled: 'ui.values.payment_cancelled',
    succeeded: 'ui.values.payment_succeeded',
  }),
  shipmentStatus: Object.freeze({
    created: 'ui.values.shipment_created',
    label_printed: 'ui.values.shipment_label_printed',
    dispatched: 'ui.values.shipment_dispatched',
    in_transit: 'ui.values.shipment_in_transit',
    delivered: 'ui.values.shipment_delivered',
    returned: 'ui.values.shipment_returned',
    lost: 'ui.values.shipment_lost',
    cancelled: 'ui.values.shipment_cancelled',
  }),
  gender: Object.freeze({
    female: 'ui.values.gender_female',
    male: 'ui.values.gender_male',
    other: 'ui.values.gender_other',
  }),
  sampleStage: Object.freeze({
    activated: 'ui.values.stage_activated',
    office_received: 'ui.values.stage_office_received',
    shipped_to_lab: 'ui.values.stage_shipped_to_lab',
    lab_received: 'ui.values.stage_lab_received',
    uploaded: 'ui.values.stage_uploaded',
  }),
  sampleStageAction: Object.freeze({
    activated: 'ui.values.stage_action_activated',
    office_received: 'ui.values.stage_action_office_received',
    shipped_to_lab: 'ui.values.stage_action_shipped_to_lab',
    lab_received: 'ui.values.stage_action_lab_received',
    uploaded: 'ui.values.stage_action_uploaded',
  }),
});

export function codeLabel(group, value) {
  const code = String(value ?? '').trim().toLowerCase();
  const key = GROUPS[group]?.[code];
  return tx(key || 'ui.common.unknown');
}

export function statusLabel(value) {
  return codeLabel('status', value);
}

export function orderReferenceLabel(value) {
  const reference = String(value ?? '');
  if (!/^[0-9]{16}$/.test(reference)) {
    throw new Error('Order reference is not configured');
  }
  return reference.match(/.{4}/g).join(' ');
}

function vocabularyCode(value) {
  return String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
}

export function roleLabel(code) {
  return tx(`ui.roles.${vocabularyCode(code)}`);
}

export function permissionLabel(code) {
  return tx(`ui.permissions.catalogue.${vocabularyCode(code)}.label`);
}

export function permissionDescription(code) {
  return tx(`ui.permissions.catalogue.${vocabularyCode(code)}.description`);
}

export function notificationTopicLabel(code) {
  return tx(`ui.notifications.catalogue.${vocabularyCode(code)}.label`);
}

export function notificationTopicDescription(code) {
  return tx(`ui.notifications.catalogue.${vocabularyCode(code)}.description`);
}

export function notificationEventTitle(code) {
  return tx(`ui.notifications.events.${vocabularyCode(code)}.title`);
}

export function notificationEventMessage(code, variables = {}) {
  const translatedVariables = { ...variables };
  for (const field of ['role', 'from', 'to']) {
    if (translatedVariables[field]) translatedVariables[field] = roleLabel(translatedVariables[field]);
  }
  return tx(`ui.notifications.events.${vocabularyCode(code)}.message`, translatedVariables);
}

// An action the catalogue has no name for, such as a route nobody classified,
// reads as its words, without the ids its code was built from.
export function auditActionLabel(code, label) {
  if (label) return label;
  const words = String(code || '')
    .replace(/[0-9a-f]{8}_[0-9a-f]{4}_[0-9a-f]{4}_[0-9a-f]{4}_[0-9a-f]{12}/gi, '')
    .split('_').filter((word) => word && !/^\d+$/.test(word)).join(' ').toLowerCase();
  return words ? `${words[0].toUpperCase()}${words.slice(1)}` : tx('ui.audit.unnamed_action');
}
